import React, { useState,useContext } from 'react';
import { useApiFetch } from '../../utils/useApiFetch';
import { TokenContext } from '../../Context/TokenContext';
import { getApiBaseUrl } from '../../utils/Config';
import { AlertContext } from '../../Context/AlertContext';
import { useIsAuthenticated } from "../../utils/msalCompat";
import { CandidateContext } from '../../Context/CandidateContext';

function ExperienceAddForm({ updateExperienceItems, candidateID, onCancel, hasCurrent }) {
    const isAuthenticated = useIsAuthenticated();
    const apiFetch = useApiFetch();
    const { refetchCandidateDetails } = useContext(CandidateContext)
    const { idTokenClaims } = useContext(TokenContext);
    const [saving, setSaving] = useState(false);
    const baseUrl = getApiBaseUrl();
    const { showAlert } = useContext(AlertContext);

    const [formData, setFormData] = useState({
        Title: '',
        Company: '',
        Location: '',
        StartDate: '',
        EndDate: '',
        Current: false,
        About: '',
    });
    const [errors, setErrors] = useState({
        Title: '',
        Company: '',
        Location: '',
        StartDate: '',
        EndDate: '',
        Current: '',
        About: '',
    });
    const handleInputChange = (event) => {
        const { name, type, value, checked } = event.target;
        const val = type === "checkbox" ? checked : value;
      
        setFormData(prevData => ({
            ...prevData,
            [name]: val
        }));
    };

    //Validation
    const validate = () => {
        const newErrors = {};

        // Allows letters, numbers, spaces, dashes, pipes, commas, and periods
        const allowedRegex = /^[a-zA-Z0-9\s,'-.|]+$/;

        const formattedStartDate = formData.StartDate ? new Date(formData.StartDate).toISOString().split('T')[0] : '';
        const formattedEndDate = formData.EndDate ? new Date(formData.EndDate).toISOString().split('T')[0] : '';

        // Check for required fields
        if (!formData.Title) newErrors.Title = 'Title is required.';
        if (!formData.Company) newErrors.Company = 'Company is required.';
        if (!formData.Location) newErrors.Location = 'Location is required.';
        if (!formData.StartDate) newErrors.StartDate = 'Start date is required.';

        // If not checked as current, end date is required
        if (!formData.Current && !formData.EndDate) {
            newErrors.EndDate = 'End date is required.';
        }

        // Disallow invalid characters
        if (formData.Title && !allowedRegex.test(formData.Title)) {
            newErrors.Title = 'Title contains invalid characters.';
        }
        if (formData.Company && !allowedRegex.test(formData.Company)) {
            newErrors.Company = 'Company name contains invalid characters.';
        }
        if (formData.Location && !allowedRegex.test(formData.Location)) {
            newErrors.Location = 'Location contains invalid characters.';
        }

        // Ensure End Date is after Start Date
        if (formData.StartDate && formData.EndDate && new Date(formattedStartDate) > new Date(formattedEndDate)) {
            newErrors.EndDate = 'End date should be after start date.';
        }

        // If Current Role is checked, EndDate shouldn't be required or considered
        if (formData.Current) {
            delete newErrors.EndDate;
        }

        // Prevent multiple current roles
        if (hasCurrent && formData.Current) {
            newErrors.Current = 'You can only have one current role.';
        }

        // Length constraints
        if (formData.Title && formData.Title.length > 100) {
            newErrors.Title = 'Title should not exceed 100 characters.';
        }
        if (formData.Company && formData.Company.length > 100) {
            newErrors.Company = 'Company should not exceed 100 characters.';
        }
        if (formData.Location && formData.Location.length > 100) {
            newErrors.Location = 'Location should not exceed 100 characters.';
        }
        if (formData.About && formData.About.length > 1000) {
            newErrors.About = 'Description should not exceed 1000 characters.';
        }

        setErrors(newErrors);
        return Object.values(newErrors).every(error => !error);
    };



    //Submit to API
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            if (saving) {
                return;
            }
            setSaving(true);

            const dataToPost = {
                ...formData,
                CandidateID: candidateID,
                DateTimeCreated: new Date().toISOString(),
                DateTimeModified: new Date().toISOString(),
                CreatedBy: 'Admin',
                ModifiedBy: 'User',
                IsDeleted: false,
                AuthUserID: idTokenClaims.oid,
            };
            if (formData.Current) {
                dataToPost.EndDate = null;
            }
            try {
                const response = await apiFetch(
                    `${baseUrl}/api/Experience/Upsert`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(dataToPost),
                    }
                );
                const responseData = await response.json();

                if (responseData.success) {
                    showAlert('Saved successfully.', true);
                    refetchCandidateDetails();
                } else {
                    showAlert(responseData.message || 'Something went wrong. Please try again.', false);
                }
            }
            catch (error) {
                showAlert('Something went wrong. Please try again.', false);
            }
            finally {
                setSaving(false);
            }

        }
    };
    if (!isAuthenticated) {
        return <div>Please log in to access the dashboard.</div>;
    }
    return (
        <div className="experience-create-form stealth-inn">
            <form onSubmit={handleSubmit}>
                <div className="experience-item-edit">
                    <div className="experience-edit card-body-iajj">
                        <input type="hidden" className="form-control" value={candidateID} name="candidateID" readOnly />

                        <div className="row g-5">
                            <div className="col-md-6">
                                <div>
                                    <label className="form-label">Job Title</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="Title"
                                        value={formData.Title}
                                        onChange={handleInputChange} />
                                    {errors.Title && (
                                        <small className="text-danger">{errors.Title}</small>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div>
                                    <label className="form-label">Company</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="Company"
                                        value={formData.Company}
                                        onChange={handleInputChange} />
                                    {errors.Company && (
                                        <small className="text-danger">{errors.Company}</small>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div>
                                    <label className="form-label">Location</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="Location"
                                        value={formData.Location}
                                        onChange={handleInputChange} />
                                    {errors.Location && (
                                        <small className="text-danger">{errors.Location}</small>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div>
                                    <label className="form-label">Start Date</label>
                                    <input
                                        type="date"
                                        className="form-control flatpickr-input active"
                                        name="StartDate"
                                        min="1900-01-01"
                                        max="2100-12-31"
                                        value={formData.StartDate}
                                        onChange={handleInputChange} />
                                    {errors.StartDate && (
                                        <small className="text-danger">{errors.StartDate}</small>
                                    )}
                                </div>
                            </div>
                            {!formData.Current && (
                                <div className="col-md-6">
                                    <div>
                                        <label className="form-label">Completion Date</label>
                                        <input
                                            type="date"
                                            className="form-control flatpickr-input active"
                                            name="EndDate"
                                            min="1900-01-01"
                                            max="2100-12-31"
                                            value={formData.EndDate}
                                            onChange={handleInputChange} />
                                        {errors.EndDate && (
                                            <small className="text-danger">{errors.EndDate}</small>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="col-md-6">
                                <label className="form-label">Current Role</label>
                                <div className="form-check form-check-linethrough">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        name="Current"
                                        checked={formData.Current}
                                        onChange={handleInputChange} disabled={hasCurrent && !formData.Current} />
                                    {errors.Current && (
                                        <small className="text-danger">{errors.Current}</small>
                                    )}
                                </div>
                            </div>

                            <div className="col-12">
                                <div>
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        name="About"
                                        value={formData.About}
                                        onChange={handleInputChange}></textarea>
                                    {errors.About && (
                                        <small className="text-danger">{errors.About}</small>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 text-end">
                                <button type="submit" className="btn btn-sm btn-primary me-2 add-experience" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                                <button type="button" className="btn btn-sm btn-neutral me-2 cancel-add-experience" onClick={onCancel}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default ExperienceAddForm;
